import { z } from 'zod';
import type { AiModelRef } from './runtime.js';
import type { AiRoleRuntimeEnvelope } from './role-config.js';

export const aiModelPolicyTierSchema = z.enum(['simple', 'main', 'complex']);
export type AiModelPolicyTier = z.infer<typeof aiModelPolicyTierSchema>;

export const aiModelPolicySlotSchema = z
  .object({
    modelId: z.string().min(1),
  })
  .strict();

export type AiModelPolicySlot = z.infer<typeof aiModelPolicySlotSchema>;

export const aiModelPolicySchema = z
  .object({
    simple: aiModelPolicySlotSchema,
    main: aiModelPolicySlotSchema,
    complex: aiModelPolicySlotSchema,
    fallback: aiModelPolicySlotSchema.nullable().optional(),
  })
  .strict();

export type AiModelPolicy = z.infer<typeof aiModelPolicySchema>;

const CDLIT_PLATFORM_ROUTED_MODEL = ['platform', 'routed'].join('-');

export function isCdlitPlatformOwnedModelRef(ref: AiModelRef): boolean {
  return ref.provider === 'openrouter' && ref.model === CDLIT_PLATFORM_ROUTED_MODEL;
}

export function isCdlitPlatformOwnedModelId(modelId: string): boolean {
  const slash = modelId.indexOf('/');
  if (slash <= 0) return false;
  if (modelId.slice(0, slash) !== 'openrouter') return false;
  return modelId.slice(slash + 1) === CDLIT_PLATFORM_ROUTED_MODEL;
}

function platformModelIdFromRef(ref: AiModelRef): string | undefined {
  const model = ref.model.trim();
  if (!model || isCdlitPlatformOwnedModelRef(ref)) return undefined;
  if (model.includes('/')) return model;
  return undefined;
}

export function deriveModelPolicyFromLegacyRuntime(
  runtime: AiRoleRuntimeEnvelope,
): AiModelPolicy | undefined {
  if (isCdlitPlatformOwnedModelRef(runtime.primary)) {
    return undefined;
  }

  const primaryId = platformModelIdFromRef(runtime.primary);
  if (!primaryId) {
    return undefined;
  }

  const fallbackRef = runtime.fallback ?? null;
  const fallbackId = fallbackRef ? platformModelIdFromRef(fallbackRef) : undefined;

  const slot = { modelId: primaryId };
  return {
    simple: slot,
    main: slot,
    complex: slot,
    fallback: fallbackId ? { modelId: fallbackId } : null,
  };
}

export function resolveModelIdForComplexity(
  modelPolicy: AiModelPolicy,
  tier: AiModelPolicyTier,
): string {
  return modelPolicy[tier].modelId;
}

export function resolveCdlitTextGatewayModelId(
  resolved: { modelPolicy?: AiModelPolicy },
  tier: AiModelPolicyTier = 'main',
): string {
  const policy = resolved.modelPolicy;
  if (!policy) {
    throw new Error('modelPolicy is required for CDLIT platform text completion');
  }
  return resolveModelIdForComplexity(policy, tier);
}
